import sanitizeHtml from "./sanitize";

const URL_PATTERN = /(\b(https?|ftp|file):\/\/[^\s<>"']+)/gi;

const postContentSanitizeConfig = {
  ALLOWED_TAGS: [
    "b",
    "i",
    "em",
    "strong",
    "a",
    "u",
    "br",
    "p",
    "span",
    "div",
    "ul",
    "ol",
    "li",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "class"],
  ADD_ATTR: ["rel"],
};

export const decodeHtmlEntities = (html) => {
  if (!html || typeof html !== "string") return html ?? "";

  if (!/&(?:lt|gt|amp|quot|#39|#x)/i.test(html)) {
    return html;
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  return textarea.value;
};

const getDisplayUrl = (url) => {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.replace(/^www\./, "");

    if (hostname.includes("facebook.com") || hostname.includes("fb.com")) {
      if (parsed.pathname.includes("/photo")) {
        return "facebook.com/photo";
      }
      return `${hostname}${parsed.pathname}`;
    }

    const display = `${hostname}${parsed.pathname}`;
    return display.length > 60 ? `${display.slice(0, 57)}...` : display;
  } catch {
    return url.length > 60 ? `${url.slice(0, 57)}...` : url;
  }
};

export const linkifyContent = (htmlContent) => {
  if (!htmlContent) return "";

  const container = document.createElement("div");
  container.innerHTML = htmlContent;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (node.parentElement?.closest("a")) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((textNode) => {
    const text = textNode.textContent || "";
    const matches = [...text.matchAll(URL_PATTERN)];
    if (!matches.length) return;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    matches.forEach((match) => {
      const url = match[0];
      const offset = match.index ?? 0;

      if (offset > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, offset))
        );
      }

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.textContent = getDisplayUrl(url);
      fragment.appendChild(anchor);
      lastIndex = offset + url.length;
    });

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  });

  return container.innerHTML;
};

export const addTargetBlankToAnchors = (html) => {
  if (!html) return "";

  const div = document.createElement("div");
  div.innerHTML = html;

  div.querySelectorAll("a").forEach((anchor) => {
    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
  });

  return div.innerHTML;
};

export const replaceDynamicValues = (content, dynamicValues) => {
  if (!content || !dynamicValues) return content;

  let result = content;

  dynamicValues.forEach((item) => {
    const placeholder = `{${item?.dVno}}`;
    const displayName = item.displayName ?? item.display ?? "";
    const entityId = item.entity ?? item.entityID ?? "";
    const replacement =
      item.type === "UserMention"
        ? `<a href="/user/${entityId}" class="user-mention">@${displayName}</a>`
        : item.type === "Hashtag"
          ? `<a href="/hashtag/${displayName}" class="hashtag">#${displayName}</a>`
          : item.type === "CompanyMention"
            ? `<a href="/Company/${entityId}" class="hashtag">@${displayName}</a>`
            : item.displayName;

    result = result.replaceAll(placeholder, replacement);
  });

  return result;
};

export const formatPostContent = (content, dynamicValues = []) => {
  if (!content) return "";

  const decoded = decodeHtmlEntities(content);
  const withDynamicValues = replaceDynamicValues(decoded, dynamicValues);
  const cleanedContent = withDynamicValues.replace(/\{\d+\}/g, "");
  const linked = linkifyContent(cleanedContent);
  const withAnchors = addTargetBlankToAnchors(linked);

  return sanitizeHtml(withAnchors, postContentSanitizeConfig);
};

export const formatExpandedPostContent = (
  expandedContent,
  fallbackDynamicValues = []
) => {
  let resolvedContent = expandedContent;
  let resolvedDynamicValues = fallbackDynamicValues;

  if (expandedContent && typeof expandedContent === "object") {
    resolvedContent =
      expandedContent?.postContent ||
      expandedContent?.mainContent ||
      expandedContent?.content ||
      expandedContent?.postMainContent ||
      "";

    if (
      Array.isArray(expandedContent?.dynamicValues) &&
      expandedContent.dynamicValues.length
    ) {
      resolvedDynamicValues = expandedContent.dynamicValues;
    }
  }

  return formatPostContent(resolvedContent || "", resolvedDynamicValues || []);
};

export default formatPostContent;
